import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { 
  Plus, GripVertical, Trash2, Clock, Target, RotateCcw, 
  Search, ChevronDown, ChevronUp, Save, X, Dumbbell,
  Play, BookOpen, Layers, Upload, FileText, Check, AlertCircle, Eye
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Draggable Exercise Item from Library
const DraggableExercise = ({ exercise, onAdd }) => {
  const getTypeColor = (type) => {
    const colors = {
      'Warm-up': 'bg-orange-600',
      'Breath Hold': 'bg-blue-600',
      'Dynamic': 'bg-teal-600',
      'Static': 'bg-purple-600',
      'Recovery': 'bg-green-600',
      'Cool Down': 'bg-indigo-600',
      'Technique': 'bg-pink-600'
    };
    return colors[type] || 'bg-gray-600';
  };

  return (
    <div className="p-3 bg-blue-800/40 rounded-lg border border-blue-700/30 hover:border-blue-500 transition-colors cursor-pointer group">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h4 className="text-blue-200 text-sm font-medium">{exercise.name}</h4>
          <div className="flex flex-wrap gap-1 mt-1">
            <Badge className={`${getTypeColor(exercise.type)} text-white text-xs`}>
              {exercise.type}
            </Badge>
            {exercise.duration_minutes && (
              <span className="text-xs text-blue-400 flex items-center">
                <Clock size={10} className="mr-1" />{exercise.duration_minutes}min
              </span>
            )}
            {exercise.distance_meters && (
              <span className="text-xs text-blue-400 flex items-center">
                <Target size={10} className="mr-1" />{exercise.distance_meters}m
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="opacity-0 group-hover:opacity-100 transition-opacity text-teal-400 hover:text-teal-300 hover:bg-teal-900/50"
          onClick={() => onAdd(exercise)}
        >
          <Plus size={16} />
        </Button>
      </div>
    </div>
  );
};

// Sortable Block Item in Session Timeline
const SortableBlock = ({ block, index, onRemove, onUpdate, isExpanded, onToggleExpand }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getTypeColor = (type) => {
    const colors = {
      'Warm-up': 'border-l-orange-500 bg-orange-900/20',
      'Breath Hold': 'border-l-blue-500 bg-blue-900/20',
      'Dynamic': 'border-l-teal-500 bg-teal-900/20',
      'Static': 'border-l-purple-500 bg-purple-900/20',
      'Recovery': 'border-l-green-500 bg-green-900/20',
      'Cool Down': 'border-l-indigo-500 bg-indigo-900/20',
      'Technique': 'border-l-pink-500 bg-pink-900/20'
    };
    return colors[block.type] || 'border-l-gray-500 bg-gray-900/20';
  };

  const totalBlockDuration = block.exercises?.reduce((sum, ex) => sum + (ex.duration_minutes || 0), 0) || block.duration_minutes || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-l-4 rounded-lg p-3 mb-2 ${getTypeColor(block.type)} ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}`}
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-blue-400 hover:text-blue-300 p-1"
        >
          <GripVertical size={18} />
        </button>
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs bg-blue-700/50 text-blue-200 px-2 py-0.5 rounded">
              #{index + 1}
            </span>
            <Input
              value={block.name}
              onChange={(e) => onUpdate(block.id, { name: e.target.value })}
              className="bg-transparent border-none text-blue-200 font-medium p-0 h-auto focus:ring-0"
              placeholder="Block name..."
            />
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-blue-400">
            <span className="flex items-center">
              <Clock size={12} className="mr-1" />
              {totalBlockDuration} min
            </span>
            <span>{block.exercises?.length || 0} exercises</span>
          </div>
        </div>
        
        <button
          onClick={() => onToggleExpand(block.id)}
          className="text-blue-400 hover:text-blue-300 p-1"
        >
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        
        <button
          onClick={() => onRemove(block.id)}
          className="text-red-400 hover:text-red-300 p-1"
        >
          <Trash2 size={18} />
        </button>
      </div>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-blue-700/30">
          {/* Block type selector */}
          <div className="mb-3">
            <Label className="text-blue-300 text-xs">Block Type</Label>
            <Select value={block.type} onValueChange={(value) => onUpdate(block.id, { type: value })}>
              <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white text-sm h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600">
                <SelectItem value="Warm-up">Warm-up</SelectItem>
                <SelectItem value="Breath Hold">Breath Hold</SelectItem>
                <SelectItem value="Dynamic">Dynamic</SelectItem>
                <SelectItem value="Static">Static</SelectItem>
                <SelectItem value="Recovery">Recovery</SelectItem>
                <SelectItem value="Cool Down">Cool Down</SelectItem>
                <SelectItem value="Technique">Technique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Exercises in block */}
          <div className="space-y-2">
            {block.exercises?.map((exercise, exIndex) => (
              <div key={exercise.id || exIndex} className="flex items-center gap-2 p-2 bg-blue-800/30 rounded">
                <span className="text-xs text-blue-400 w-6">{exIndex + 1}.</span>
                <span className="flex-1 text-sm text-blue-200">{exercise.name}</span>
                {exercise.duration_minutes && (
                  <span className="text-xs text-blue-400">{exercise.duration_minutes}min</span>
                )}
                {exercise.repetitions && (
                  <span className="text-xs text-blue-400">{exercise.repetitions}x</span>
                )}
                <button
                  onClick={() => {
                    const newExercises = block.exercises.filter((_, i) => i !== exIndex);
                    onUpdate(block.id, { exercises: newExercises });
                  }}
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {(!block.exercises || block.exercises.length === 0) && (
              <p className="text-xs text-blue-400 italic text-center py-2">
                Add exercises from the library →
              </p>
            )}
          </div>
          
          {/* Notes */}
          <div className="mt-3">
            <Label className="text-blue-300 text-xs">Notes</Label>
            <Textarea
              value={block.notes || ''}
              onChange={(e) => onUpdate(block.id, { notes: e.target.value })}
              placeholder="Block notes..."
              className="bg-blue-800/50 border-blue-600 text-white text-sm h-16"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Drag Overlay for visual feedback
const DragOverlayContent = ({ block }) => {
  if (!block) return null;
  
  return (
    <div className="p-3 bg-blue-700 rounded-lg border-2 border-blue-500 shadow-xl">
      <div className="flex items-center gap-2">
        <GripVertical size={18} className="text-blue-300" />
        <span className="text-blue-100 font-medium">{block.name}</span>
      </div>
    </div>
  );
};

const SessionBuilderTab = ({ t, currentUser, onSaveSession }) => {
  // Session state
  const [sessionName, setSessionName] = useState('');
  const [sessionDiscipline, setSessionDiscipline] = useState('Mix');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState('19:00');
  const [sessionEndTime, setSessionEndTime] = useState('20:30');
  const [sessionNotes, setSessionNotes] = useState('');
  
  // Blocks state
  const [blocks, setBlocks] = useState([]);
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  
  // Library state
  const [exercises, setExercises] = useState([]);
  const [customExercises, setCustomExercises] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [libraryTab, setLibraryTab] = useState('prebuilt'); // 'prebuilt', 'custom', 'all'
  
  // PDF Parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const pdfInputRef = useRef(null);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch exercises
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        // Fetch prebuilt exercises
        const prebuiltResponse = await axios.get(`${API_BASE_URL}/api/exercise-library`);
        const exercisesByCategory = prebuiltResponse.data.exercises || {};
        const flattenedExercises = [];
        Object.entries(exercisesByCategory).forEach(([category, exs]) => {
          exs.forEach(exercise => {
            flattenedExercises.push({ ...exercise, category, source: 'prebuilt' });
          });
        });
        setExercises(flattenedExercises);
        
        // Fetch custom exercises
        const customResponse = await axios.get(`${API_BASE_URL}/api/custom-exercises`, {
          params: { user_id: currentUser?.id, include_public: true }
        });
        setCustomExercises((customResponse.data.exercises || []).map(e => ({ ...e, source: 'custom' })));
      } catch (error) {
        console.error('Error fetching exercises:', error);
      }
    };
    
    fetchExercises();
  }, [currentUser?.id]);

  // Filter exercises
  const filteredExercises = useMemo(() => {
    let allExercises = [];
    if (libraryTab === 'prebuilt' || libraryTab === 'all') {
      allExercises = [...allExercises, ...exercises];
    }
    if (libraryTab === 'custom' || libraryTab === 'all') {
      allExercises = [...allExercises, ...customExercises];
    }
    
    return allExercises.filter(exercise => {
      if (searchQuery && !exercise.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterType !== 'all' && exercise.type !== filterType) {
        return false;
      }
      return true;
    });
  }, [exercises, customExercises, libraryTab, searchQuery, filterType]);

  // Calculate total duration
  const totalDuration = useMemo(() => {
    return blocks.reduce((sum, block) => {
      const blockDuration = block.exercises?.reduce((s, ex) => s + (ex.duration_minutes || 0), 0) || block.duration_minutes || 0;
      return sum + blockDuration;
    }, 0);
  }, [blocks]);

  // Generate unique ID using ref counter only
  const idCounter = React.useRef(0);
  const generateId = (prefix) => {
    idCounter.current += 1;
    return `${prefix}-${idCounter.current}`;
  };

  // Add new block
  const addNewBlock = (type = 'Warm-up') => {
    const newBlockId = generateId('block');
    const newBlock = {
      id: newBlockId,
      name: `${type} Block`,
      type,
      duration_minutes: 10,
      exercises: [],
      notes: ''
    };
    setBlocks(prevBlocks => [...prevBlocks, newBlock]);
    setExpandedBlocks(prev => new Set([...prev, newBlockId]));
  };

  // Add exercise to selected block or create new block
  const addExerciseToSession = (exercise, targetBlockId = null) => {
    const newExerciseId = generateId('ex');
    const exerciseWithId = { ...exercise, id: newExerciseId };
    
    if (targetBlockId) {
      // Add to specific block
      setBlocks(prevBlocks => prevBlocks.map(block => {
        if (block.id === targetBlockId) {
          return {
            ...block,
            exercises: [...(block.exercises || []), exerciseWithId]
          };
        }
        return block;
      }));
    } else {
      // Find matching block type or create new
      setBlocks(prevBlocks => {
        const matchingBlock = prevBlocks.find(b => b.type === exercise.type);
        if (matchingBlock) {
          return prevBlocks.map(block => {
            if (block.id === matchingBlock.id) {
              return {
                ...block,
                exercises: [...(block.exercises || []), exerciseWithId]
              };
            }
            return block;
          });
        } else {
          // Create new block with exercise
          const newBlockId = generateId('block');
          const newBlock = {
            id: newBlockId,
            name: `${exercise.type} Block`,
            type: exercise.type,
            duration_minutes: exercise.duration_minutes || 10,
            exercises: [exerciseWithId],
            notes: ''
          };
          setTimeout(() => {
            setExpandedBlocks(prev => new Set([...prev, newBlockId]));
          }, 0);
          return [...prevBlocks, newBlock];
        }
      });
    }
  };

  // Remove block
  const removeBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.id !== blockId));
    const newExpanded = new Set(expandedBlocks);
    newExpanded.delete(blockId);
    setExpandedBlocks(newExpanded);
  };

  // Update block
  const updateBlock = (blockId, updates) => {
    setBlocks(blocks.map(block => 
      block.id === blockId ? { ...block, ...updates } : block
    ));
  };

  // Toggle block expansion
  const toggleBlockExpand = (blockId) => {
    const newExpanded = new Set(expandedBlocks);
    if (newExpanded.has(blockId)) {
      newExpanded.delete(blockId);
    } else {
      newExpanded.add(blockId);
    }
    setExpandedBlocks(newExpanded);
  };

  // Handle drag end for reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Save session
  const handleSaveSession = async () => {
    if (!sessionName.trim()) {
      alert(t('sessionNameRequired') || 'Session name is required');
      return;
    }
    
    if (blocks.length === 0) {
      alert(t('addBlocksFirst') || 'Please add at least one block');
      return;
    }

    // Format training_date as ISO datetime
    const trainingDateTime = sessionDate 
      ? new Date(`${sessionDate}T${sessionStartTime}:00`).toISOString()
      : new Date().toISOString();
    
    const sessionData = {
      name: sessionName,
      discipline: sessionDiscipline,
      training_date: trainingDateTime,
      start_time: `${sessionStartTime}:00`,
      end_time: `${sessionEndTime}:00`,
      exercise_blocks: blocks.map((block, index) => {
        const blockDuration = block.exercises?.reduce((s, ex) => s + (ex.duration_minutes || 0), 0) || block.duration_minutes || 10;
        return {
          id: block.id,
          name: block.name,
          total_duration_minutes: blockDuration,
          exercises: (block.exercises || []).map(ex => ({
            id: ex.id,
            name: ex.name,
            type: ex.type || 'Warm-up',
            description: ex.description || '',
            duration_minutes: ex.duration_minutes || null,
            repetitions: ex.repetitions || null,
            distance_meters: ex.distance_meters || null,
            rest_seconds: ex.rest_seconds || null,
            instructions: ex.instructions || null,
            difficulty_level: ex.difficulty_level || 1
          })),
          notes: block.notes || null
        };
      }),
      notes: sessionNotes,
      location: {
        building_name: '',
        organization: '',
        street_address: '',
        city: '',
        postal_code: '',
        country: ''
      },
      pool_specs: {
        type: '25m',
        length_meters: 25,
        number_of_lanes: 6,
        depth_shallow_end: 1.2,
        depth_deep_end: 1.8,
        max_participants: 12
      },
      season: 'Spring'
    };

    try {
      await axios.post(`${API_BASE_URL}/api/training-sessions`, sessionData);
      alert(t('sessionSaved') || 'Session saved successfully!');
      
      // Reset form
      setSessionName('');
      setBlocks([]);
      setExpandedBlocks(new Set());
      setSessionNotes('');
      
      if (onSaveSession) {
        onSaveSession();
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(t('errorSavingSession') || 'Error saving session. Please try again.');
    }
  };

  // Clear all
  const handleClearAll = () => {
    if (window.confirm(t('confirmClearAll') || 'Are you sure you want to clear all blocks?')) {
      setBlocks([]);
      setExpandedBlocks(new Set());
    }
  };

  // PDF Parsing handlers
  const handlePdfFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isPdf = fileName.endsWith('.pdf');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isPdf && !isExcel) {
      setPdfError(t('pdfOrExcelOnly') || 'Only PDF or Excel files are supported');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setPdfError(t('fileTooLarge') || 'File too large (max 10MB)');
      return;
    }

    setIsParsing(true);
    setPdfError(null);
    setParsedResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Choose endpoint based on file type
      const endpoint = isPdf 
        ? `${API_BASE_URL}/api/parse-training-pdf`
        : `${API_BASE_URL}/api/parse-training-excel`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setParsedResult(response.data);
        setShowPdfModal(true);
      } else {
        setPdfError(t('fileParseError') || 'Error parsing file');
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      setPdfError(error.response?.data?.detail || t('fileParseError') || 'Error parsing file');
    } finally {
      setIsParsing(false);
      // Reset the file input
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  const handleImportParsedBlocks = () => {
    if (!parsedResult?.blocks?.length) return;

    // Generate unique IDs for imported blocks and exercises
    const importedBlocks = parsedResult.blocks.map(block => {
      const newBlockId = generateId('block');
      return {
        id: newBlockId,
        name: block.name,
        type: block.type,
        duration_minutes: block.duration_minutes || 10,
        exercises: (block.exercises || []).map(ex => ({
          ...ex,
          id: generateId('ex')
        })),
        notes: block.notes || ''
      };
    });

    setBlocks(prevBlocks => [...prevBlocks, ...importedBlocks]);
    
    // Expand all newly imported blocks
    setExpandedBlocks(prev => {
      const newExpanded = new Set(prev);
      importedBlocks.forEach(b => newExpanded.add(b.id));
      return newExpanded;
    });

    setShowPdfModal(false);
    setParsedResult(null);
  };

  const handleImportSingleExercise = (exercise) => {
    addExerciseToSession(exercise);
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setParsedResult(null);
    setPdfError(null);
  };

  const activeBlock = activeId ? blocks.find(b => b.id === activeId) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200 flex items-center">
          <Layers className="mr-3" size={28} />
          {t('sessionBuilder') || 'Session Builder'}
        </h2>
        <div className="flex gap-2">
          {/* Hidden PDF input */}
          <input
            type="file"
            ref={pdfInputRef}
            accept=".pdf,.xlsx,.xls"
            onChange={handlePdfFileSelect}
            className="hidden"
          />
          <Button
            onClick={() => pdfInputRef.current?.click()}
            variant="outline"
            className="border-purple-500 text-purple-300 hover:bg-purple-900/50"
            disabled={isParsing}
          >
            {isParsing ? (
              <>
                <RotateCcw className="mr-2 animate-spin" size={16} />
                {t('parsingFile') || 'Parsing...'}
              </>
            ) : (
              <>
                <Upload className="mr-2" size={16} />
                {t('importFromFile') || 'Import from File'}
              </>
            )}
          </Button>
          <Button
            onClick={handleClearAll}
            variant="outline"
            className="border-red-500 text-red-300 hover:bg-red-900/50"
            disabled={blocks.length === 0}
          >
            <Trash2 className="mr-2" size={16} />
            {t('clearAll') || 'Clear All'}
          </Button>
          <Button
            onClick={handleSaveSession}
            className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
          >
            <Save className="mr-2" size={16} />
            {t('saveSession') || 'Save Session'}
          </Button>
        </div>
      </div>

      {/* Session Info Bar */}
      <Card className="bg-gradient-to-r from-blue-800/40 to-teal-800/40 border-blue-700/30">
        <CardContent className="p-4">
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-2">
              <Label className="text-blue-300 text-xs">{t('sessionName')}</Label>
              <Input
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder={t('enterSessionName') || 'Enter session name...'}
                className="bg-blue-800/50 border-blue-600 text-white"
              />
            </div>
            <div>
              <Label className="text-blue-300 text-xs">{t('discipline')}</Label>
              <Select value={sessionDiscipline} onValueChange={setSessionDiscipline}>
                <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-blue-800 border-blue-600">
                  <SelectItem value="Mix">Mix</SelectItem>
                  <SelectItem value="STA">STA</SelectItem>
                  <SelectItem value="DYN">DYN</SelectItem>
                  <SelectItem value="DYNBF">DYNBF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-blue-300 text-xs">{t('date')}</Label>
              <Input
                type="date"
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
                className="bg-blue-800/50 border-blue-600 text-white"
              />
            </div>
            <div>
              <Label className="text-blue-300 text-xs">{t('startTime')}</Label>
              <Input
                type="time"
                value={sessionStartTime}
                onChange={(e) => setSessionStartTime(e.target.value)}
                className="bg-blue-800/50 border-blue-600 text-white"
              />
            </div>
            <div>
              <Label className="text-blue-300 text-xs">{t('endTime')}</Label>
              <Input
                type="time"
                value={sessionEndTime}
                onChange={(e) => setSessionEndTime(e.target.value)}
                className="bg-blue-800/50 border-blue-600 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Session Timeline */}
        <div className="col-span-2">
          <Card className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 border-blue-700/30 min-h-[500px]">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-blue-200 flex items-center">
                  <Play className="mr-2" size={20} />
                  {t('sessionTimeline') || 'Session Timeline'}
                </CardTitle>
                <div className="flex items-center gap-4">
                  <Badge className="bg-blue-600 text-white">
                    <Clock size={14} className="mr-1" />
                    {totalDuration} min
                  </Badge>
                  <Badge variant="secondary" className="bg-purple-700/50 text-purple-200">
                    {blocks.length} {t('blocks') || 'blocks'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add Block Buttons */}
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-blue-700/30">
                <span className="text-blue-300 text-sm self-center mr-2">{t('addBlock') || 'Add Block'}:</span>
                {['Warm-up', 'Dynamic', 'Static', 'Breath Hold', 'Recovery', 'Cool Down'].map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant="outline"
                    onClick={() => addNewBlock(type)}
                    className="border-blue-500 text-blue-300 hover:bg-blue-700/50 text-xs"
                  >
                    <Plus size={12} className="mr-1" />
                    {type}
                  </Button>
                ))}
              </div>

              {/* Sortable Blocks */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={blocks.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {blocks.length > 0 ? (
                    <div className="space-y-2">
                      {blocks.map((block, index) => (
                        <SortableBlock
                          key={block.id}
                          block={block}
                          index={index}
                          onRemove={removeBlock}
                          onUpdate={updateBlock}
                          isExpanded={expandedBlocks.has(block.id)}
                          onToggleExpand={toggleBlockExpand}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-blue-400">
                      <Dumbbell size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg mb-2">{t('noBlocksYet') || 'No blocks yet'}</p>
                      <p className="text-sm opacity-75">
                        {t('addBlocksHint') || 'Click the buttons above to add training blocks, or drag exercises from the library'}
                      </p>
                    </div>
                  )}
                </SortableContext>
                
                <DragOverlay>
                  <DragOverlayContent block={activeBlock} />
                </DragOverlay>
              </DndContext>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Exercise Library */}
        <div className="col-span-1">
          <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-blue-200 flex items-center">
                <BookOpen className="mr-2" size={20} />
                {t('exerciseLibrary') || 'Exercise Library'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Library Tabs */}
              <div className="flex gap-1 mb-3 bg-blue-900/50 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={libraryTab === 'prebuilt' ? 'default' : 'ghost'}
                  onClick={() => setLibraryTab('prebuilt')}
                  className={`flex-1 text-xs ${libraryTab === 'prebuilt' ? 'bg-blue-600' : 'text-blue-300'}`}
                >
                  {t('prebuiltExercises') || 'Pre-built'}
                </Button>
                <Button
                  size="sm"
                  variant={libraryTab === 'custom' ? 'default' : 'ghost'}
                  onClick={() => setLibraryTab('custom')}
                  className={`flex-1 text-xs ${libraryTab === 'custom' ? 'bg-blue-600' : 'text-blue-300'}`}
                >
                  {t('myExercises') || 'My Exercises'}
                </Button>
                <Button
                  size="sm"
                  variant={libraryTab === 'all' ? 'default' : 'ghost'}
                  onClick={() => setLibraryTab('all')}
                  className={`flex-1 text-xs ${libraryTab === 'all' ? 'bg-blue-600' : 'text-blue-300'}`}
                >
                  {t('all') || 'All'}
                </Button>
              </div>

              {/* Search & Filter */}
              <div className="space-y-2 mb-3">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400" size={14} />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('searchExercises') || 'Search...'}
                    className="pl-8 bg-blue-800/50 border-blue-600 text-white text-sm h-8"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white text-sm h-8">
                    <SelectValue placeholder={t('filterByType') || 'Filter by type'} />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    <SelectItem value="all">{t('all') || 'All Types'}</SelectItem>
                    <SelectItem value="Warm-up">Warm-up</SelectItem>
                    <SelectItem value="Dynamic">Dynamic</SelectItem>
                    <SelectItem value="Static">Static</SelectItem>
                    <SelectItem value="Breath Hold">Breath Hold</SelectItem>
                    <SelectItem value="Recovery">Recovery</SelectItem>
                    <SelectItem value="Cool Down">Cool Down</SelectItem>
                    <SelectItem value="Technique">Technique</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Exercise List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {filteredExercises.map((exercise) => (
                  <DraggableExercise
                    key={exercise.id}
                    exercise={exercise}
                    onAdd={addExerciseToSession}
                  />
                ))}
                {filteredExercises.length === 0 && (
                  <p className="text-center text-blue-400 text-sm py-4">
                    {t('noExercisesFound') || 'No exercises found'}
                  </p>
                )}
              </div>

              {/* Exercise count */}
              <div className="mt-3 pt-3 border-t border-blue-700/30 text-center">
                <Badge variant="secondary" className="bg-blue-700/50 text-blue-200">
                  {filteredExercises.length} {t('exercises') || 'exercises'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Session Notes */}
      <Card className="bg-gradient-to-r from-blue-800/40 to-teal-800/40 border-blue-700/30">
        <CardContent className="p-4">
          <Label className="text-blue-300">{t('additionalNotes') || 'Additional Notes'}</Label>
          <Textarea
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            placeholder={t('sessionNotesPlaceholder') || 'Add any notes for this training session...'}
            className="bg-blue-800/50 border-blue-600 text-white mt-2"
            rows={3}
          />
        </CardContent>
      </Card>

      {/* PDF Error Alert */}
      {pdfError && (
        <div className="fixed bottom-4 right-4 bg-red-900/90 border border-red-500 text-red-200 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <AlertCircle size={20} />
          <span>{pdfError}</span>
          <button onClick={() => setPdfError(null)} className="text-red-300 hover:text-white">
            <X size={18} />
          </button>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfModal && parsedResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-xl border border-blue-600 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-blue-700 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-blue-200 flex items-center">
                  <FileText className="mr-2" size={24} />
                  {t('fileParsed') || 'File Parsed Successfully'}
                </h3>
                <p className="text-blue-400 text-sm mt-1">
                  {parsedResult.filename} • {parsedResult.total_exercises_found} {t('exercisesFound') || 'exercises found'} • {parsedResult.total_blocks} {t('blocksDetected') || 'blocks detected'}
                </p>
              </div>
              <button 
                onClick={handleClosePdfModal}
                className="text-blue-400 hover:text-white p-2 hover:bg-blue-800/50 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Parsed Blocks Section */}
              {parsedResult.blocks?.length > 0 && (
                <div>
                  <h4 className="text-blue-300 font-semibold mb-3 flex items-center">
                    <Layers className="mr-2" size={18} />
                    {t('parsedBlocks') || 'Parsed Blocks'} ({parsedResult.blocks.length})
                  </h4>
                  <div className="space-y-2">
                    {parsedResult.blocks.map((block, index) => (
                      <div 
                        key={index} 
                        className="bg-blue-800/40 rounded-lg p-3 border border-blue-700/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-blue-200 font-medium">{block.name}</span>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-600 text-white text-xs">
                              {block.type}
                            </Badge>
                            {block.duration_minutes > 0 && (
                              <span className="text-xs text-blue-400 flex items-center">
                                <Clock size={12} className="mr-1" />{block.duration_minutes}min
                              </span>
                            )}
                          </div>
                        </div>
                        {block.exercises?.length > 0 && (
                          <div className="space-y-1">
                            {block.exercises.map((ex, exIdx) => (
                              <div 
                                key={exIdx} 
                                className="flex items-center justify-between bg-blue-900/40 rounded px-2 py-1.5 text-sm"
                              >
                                <span className="text-blue-300">{ex.name}</span>
                                <div className="flex items-center gap-2">
                                  {ex.duration_minutes && (
                                    <span className="text-xs text-blue-400">{ex.duration_minutes}min</span>
                                  )}
                                  {ex.distance_meters && (
                                    <span className="text-xs text-blue-400">{ex.distance_meters}m</span>
                                  )}
                                  {ex.repetitions && (
                                    <span className="text-xs text-blue-400">{ex.repetitions}x</span>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-teal-400 hover:text-teal-300 hover:bg-teal-900/50"
                                    onClick={() => handleImportSingleExercise(ex)}
                                  >
                                    <Plus size={14} />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw Text Preview (collapsible) */}
              {parsedResult.raw_text_preview && (
                <details className="bg-blue-800/30 rounded-lg border border-blue-700/30">
                  <summary className="cursor-pointer p-3 text-blue-300 hover:text-blue-200 flex items-center">
                    <Eye className="mr-2" size={16} />
                    {t('rawTextPreview') || 'Raw Text Preview'}
                  </summary>
                  <div className="p-3 pt-0">
                    <pre className="text-xs text-blue-400 whitespace-pre-wrap max-h-48 overflow-y-auto bg-blue-900/50 p-2 rounded">
                      {parsedResult.raw_text_preview}
                    </pre>
                  </div>
                </details>
              )}

              {/* No exercises found message */}
              {(!parsedResult.exercises || parsedResult.exercises.length === 0) && (
                <div className="text-center py-8 text-blue-400">
                  <AlertCircle size={40} className="mx-auto mb-3 opacity-50" />
                  <p>{t('noExercisesExtracted') || 'No exercises could be extracted from this PDF'}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-blue-700 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleClosePdfModal}
                className="border-blue-500 text-blue-300 hover:bg-blue-800/50"
              >
                {t('closePreview') || 'Close'}
              </Button>
              {parsedResult.blocks?.length > 0 && (
                <Button
                  onClick={handleImportParsedBlocks}
                  className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white"
                >
                  <Check className="mr-2" size={16} />
                  {t('importAllBlocks') || 'Import All Blocks'} ({parsedResult.total_exercises_found} {t('exercises') || 'exercises'})
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionBuilderTab;
